import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePermissions } from '@Core/Hooks/usePermissions'
import {
    DirectoryAccount,
    LinkConflict,
} from '@User/Data/DataSource/API/Entity/Identity'
import { identityAPI } from '@User/Data/DataSource/API/IdentityAPI'
import { identityErrorMessage } from '@User/Hooks/useIdentityLinking/useIdentityLinking'
import { DBUser } from '@User/Domain/Model/User'
import { roles } from '@User/roles'
import { useState } from 'react'
import { toast } from 'react-toastify'

const MICROSOFT_PROVIDER = 'microsoft.com'

export const microsoftAccountOf = (user: DBUser | null) =>
    [...(user?.providerData ?? []), ...(user?.firebase?.providerData ?? [])].find(
        (provider) => provider.providerId === MICROSOFT_PROVIDER
    )

const conflictMessage = (error: unknown) => {
    const data = (error as { response?: { data?: LinkConflict } })?.response?.data
    if (data?.code === 'MS_ACCOUNT_ALREADY_LINKED') {
        const owner = data.linkedTo?.name || data.linkedTo?.mail || data.linkedTo?.uid
        return `Essa conta Microsoft já está vinculada a ${owner}.`
    }
    if (data?.code === 'USER_ALREADY_LINKED') {
        return 'Este cadastro já tem outra conta Microsoft. Desvincule antes.'
    }
    if (data?.code === 'LAST_PROVIDER') {
        return 'Não é possível desvincular: é a única forma de login deste usuário.'
    }
    return null
}

interface Props {
    user: DBUser
    onChanged: () => void | Promise<void>
}

export const MicrosoftAccountSection = ({ user, onChanged }: Props) => {
    const { hasPermission } = usePermissions()
    const canEdit = hasPermission(roles.user.permissions.edit.key)
    const linked = microsoftAccountOf(user)
    const [search, setSearch] = useState(user.mail ?? '')
    const [results, setResults] = useState<DirectoryAccount[] | null>(null)
    const [busy, setBusy] = useState(false)

    const run = async (action: () => Promise<unknown>, success: string, failure: string) => {
        setBusy(true)
        try {
            await action()
            toast.success(success)
            setResults(null)
            await onChanged()
        } catch (error) {
            console.error(error)
            toast.error(conflictMessage(error) ?? identityErrorMessage(error, failure))
        }
        setBusy(false)
    }

    const handleSearch = async (event: { preventDefault: () => void }) => {
        event.preventDefault()
        setBusy(true)
        try {
            setResults(await identityAPI.searchDirectory(search))
        } catch (error) {
            console.error(error)
            toast.error(identityErrorMessage(error, 'Erro ao buscar no diretório Microsoft'))
        }
        setBusy(false)
    }

    return (
        <section className="flex flex-col gap-3" data-testid="microsoft-account-section">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold">Conta Microsoft</h2>
                {linked ? (
                    <Badge>Vinculado à Microsoft</Badge>
                ) : (
                    <Badge variant="outline">Pendente</Badge>
                )}
            </div>

            {linked && (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>{linked.email || linked.uid}</span>
                    {canEdit && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() =>
                                run(
                                    () => identityAPI.unlinkMicrosoft(user.uid),
                                    'Conta Microsoft desvinculada',
                                    'Erro ao desvincular a conta Microsoft'
                                )
                            }
                        >
                            Desvincular
                        </Button>
                    )}
                </div>
            )}

            {!linked && canEdit && (
                // Not a <form>: this section renders inside the UserDetails form.
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        aria-label="Buscar conta no diretório Microsoft"
                        className="max-w-sm"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') handleSearch(event)
                        }}
                        placeholder="Nome ou e-mail no diretório"
                    />
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy || !search.trim()}
                        onClick={handleSearch}
                    >
                        Buscar
                    </Button>
                </div>
            )}

            {results && !linked && (
                <ul className="flex flex-col gap-2 text-sm">
                    {!results.length && <li>Nenhuma conta encontrada.</li>}
                    {results.map((account) => (
                        <li key={account.msId} className="flex flex-wrap items-center gap-3">
                            <span className="font-medium">{account.displayName}</span>
                            <span className="text-muted-foreground">
                                {account.mail || account.userPrincipalName}
                            </span>
                            {!account.accountEnabled && <Badge variant="destructive">Desativada</Badge>}
                            {account.linkedUid && account.linkedUid !== user.uid ? (
                                <Badge variant="secondary">Já vinculada a outro cadastro</Badge>
                            ) : (
                                <Button
                                    type="button"
                                    size="sm"
                                    disabled={busy || !account.accountEnabled}
                                    onClick={() =>
                                        run(
                                            () => identityAPI.linkMicrosoft(user.uid, account.msId),
                                            'Conta Microsoft vinculada',
                                            'Erro ao vincular a conta Microsoft'
                                        )
                                    }
                                >
                                    Vincular
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
